import { Moon, Sun } from "lucide-react";
import { m } from "@/paraglide/messages";

/**
 * Sun/moon theme toggle. Default follows the OS (applied pre-paint by the
 * blocking THEME_SCRIPT in __root.tsx); a click pins the opposite theme in
 * localStorage. Pinning back to the OS's current theme clears the override
 * so the site follows the system again.
 *
 * Icons are CSS-driven (dark: variant) instead of state-driven, so the
 * server-rendered icon always matches the pre-paint theme class — no
 * hydration flicker and no mount effect needed.
 */
export default function ThemeToggle() {
  const toggle = () => {
    const root = document.documentElement;
    const next = root.classList.contains("dark") ? "light" : "dark";
    const system = window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
    try {
      if (next === system) {
        localStorage.removeItem("toolverse-theme");
      } else {
        localStorage.setItem("toolverse-theme", next);
      }
    } catch {
      // localStorage unavailable (private mode) — theme still applies for the session
    }
    root.classList.remove("light-theme", "dark-theme", "dark");
    root.classList.add(next === "dark" ? "dark-theme" : "light-theme");
    if (next === "dark") root.classList.add("dark");
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", next === "dark" ? "#0f1729" : "#f7f8fb");
  };

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={m.nav_toggle_theme()}
      className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      <Sun className="hidden h-5 w-5 dark:block" />
      <Moon className="block h-5 w-5 dark:hidden" />
    </button>
  );
}
